import { AfterViewInit, Component, ViewChild } from '@angular/core';
import {
  IonAccordionGroup,
  ModalController,
  SelectCustomEvent
} from '@ionic/angular';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import {
  Actions,
  ofActionDispatched,
  ofActionSuccessful,
  Select,
  Store
} from '@ngxs/store';
import { TranslocoService } from '@ngneat/transloco';
import _, { cloneDeep } from 'lodash';
import { Observable } from 'rxjs';
import { ReactiveComponent } from 'src/app/components/shared/reactive-component';
import { PaymentConstant, RefundsConstants } from 'src/app/constants';
import {
  AccordionView,
  CardTerminal,
  PaymentLocation,
  PaymentRequestType,
  RefundResponse,
  RefundTender,
  RefundTransactionRequest
} from 'src/app/models';
import { BaseProvider } from 'src/app/providers';
import { ModalService } from 'src/app/services';
import { SelectPaymentLocation } from 'src/app/store/app/app.action';
import { AppSelectors } from 'src/app/store/app/app.selectors';
import { LocationSelectors } from 'src/app/store/location/location.selectors';
import { PrintTicket } from 'src/app/store/tickets/tickets.action';
import {
  AddRefundTransaction,
  ClearRefundState,
  DeleteRefundTicket,
  RefundClose,
  RefundComplete,
  RefundNetworkError
} from 'src/app/store/transaction-lookup/transaction-lookup.action';
import { TransactionLookupSelectors } from 'src/app/store/transaction-lookup/transaction-lookup.selectors';
import { GiftCardPaymentModal } from '../payments/gift-card/gift-card.modal';

@Component({
  templateUrl: './refunds.modal.html',
  styleUrls: ['./refunds.modal.scss']
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class RefundsModal extends ReactiveComponent implements AfterViewInit {
  @Select(TransactionLookupSelectors.refundTicket)
  refundTicket$: Observable<RefundResponse>;
  refundTicket: RefundResponse;
  @ViewSelectSnapshot(AppSelectors.payment_location)
  readonly selectedPaymentLocation: Readonly<PaymentLocation>;
  @ViewSelectSnapshot(LocationSelectors.getPaymentLocations)
  readonly paymentLocations: Readonly<PaymentLocation[]>;
  @ViewSelectSnapshot(AppSelectors.getFreedomPayTerminals)
  readonly freedomPayTerminals: CardTerminal[];

  @ViewChild(IonAccordionGroup, { static: true })
  accordionGroup: IonAccordionGroup;

  selectedFreedomPayTerminalId?: number;

  activeAccordionView: AccordionView = AccordionView.TENDER;
  paymentTender: RefundTender;
  canSubmitPayment = false;
  amount: number;
  allowKeyboardInput = true;
  //Variable for mobile
  showTransactions = false;
  hasOnlyOneTender = false;
  canCloseRefund = true;
  //Freedompay
  isClosingViaFreedomPayRefundVoidCompletion = false;
  isRefundInProgress = false;
  networkErrorMessage = null;

  readonly PAYMENT_TYPES = PaymentRequestType;
  readonly PAYMENT_METHODS_MAP = RefundsConstants.REFUND_METHODS_MAP;
  readonly PAYMENT_REQUEST_TYPE_MAP = PaymentConstant.PAYMENT_REQUEST_TYPE_MAP;

  constructor(
    private modalController: ModalController,
    private store: Store,
    private actions: Actions,
    private modalService: ModalService,
    private baseProvider: BaseProvider,
    private translocoService: TranslocoService
  ) {
    super();
  }

  *onSubscribe() {
    yield this.accordionGroup.ionChange.subscribe((e) => {
      if (
        e.detail.value === AccordionView.AMOUNT ||
        e.detail.value === AccordionView.TENDER
      ) {
        this.activeAccordionView = e.detail.value;
      }
    });

    yield this.refundTicket$.subscribe((refundTicket) => {
      this.refundTicket = refundTicket;
    });

    yield this.actions
      .pipe(ofActionDispatched(AddRefundTransaction))
      .subscribe(() => {
        this.isRefundInProgress = true;
      });

    yield this.actions
      .pipe(ofActionDispatched(RefundNetworkError))
      .subscribe((networkError) => {
        this.networkErrorMessage = networkError.errorMessage;
      });

    yield this.actions
      .pipe(ofActionSuccessful(AddRefundTransaction))
      .subscribe(() => {
        this.canCloseRefund = false;
        this.onCancelTender();
      });

    yield this.actions
      .pipe(ofActionSuccessful(RefundComplete))
      .subscribe(() => {
        this.isRefundInProgress = false;
      });

    yield this.actions
      .pipe(ofActionSuccessful(RefundComplete))
      .subscribe(() => {
        this.isRefundInProgress = false;
      });

    yield this.actions.pipe(ofActionSuccessful(RefundClose)).subscribe(() => {
      this.canCloseRefund = true;
      this.isClosingViaFreedomPayRefundVoidCompletion = true;
      this.isRefundInProgress = false;
      this.onCloseModal();
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.refundTicket?.available_refund_tenders.length === 1) {
        this.hasOnlyOneTender = true;
        const fundTender = _.first(this.refundTicket.available_refund_tenders);
        const gift_card_identification_number =
          fundTender.gift_card_identification_number ||
          this.refundTicket.original_gift_card_identification_number;
        this.onSelectTenderType({
          ...fundTender,
          gift_card_identification_number
        });
      }
    }, 500);
  }

  onSelectTenderType(tender: RefundTender) {
    if (tender.amount === '0') {
      return;
    }

    this.paymentTender = cloneDeep(tender);
    this.accordionGroup.value = AccordionView.AMOUNT;
    this.amount =
      typeof tender.amount === 'string' && tender.amount?.includes('-')
        ? +tender.amount
        : -tender.amount;
    //Remaining refund due may be negative. Using Match.abs will return the absolute number.
    if (
      Math.abs(this.amount) > Math.abs(this.refundTicket.remaining_refund_due)
    ) {
      this.amount = this.refundTicket.remaining_refund_due;
    }
    this.checkPaymentValid();
  }

  onCancelTender() {
    if (this.hasOnlyOneTender) {
      this.onCloseModal();
      return;
    }

    this.amount = null;
    this.paymentTender = null;
    this.accordionGroup.value = AccordionView.TENDER;
  }

  async onCloseModal() {
    const action = (isCompleted = true) => {
      const actions = [new ClearRefundState()];
      if (!isCompleted) {
        const refundTicketId =
          this.paymentTender?.type === PaymentRequestType.CREDIT_CARD_FREEDOMPAY
            ? this.refundTicket.refund_ticket_uuid
            : this.refundTicket.refund_ticket_id;
        actions.push(new DeleteRefundTicket(refundTicketId));
      }
      this.store.dispatch(actions);
      this.modalController.dismiss();
    };

    // FreedomPay refund void doesn't update status so override check to allow closing
    if (this.isClosingViaFreedomPayRefundVoidCompletion) {
      await this.baseProvider.popupAlert(
        this.translocoService.translate('pos.refunds.refundVoided'),
        this.translocoService.translate('pos.refunds.refundVoidedMessage'),
        () => action(false)
      );
      return;
    }

    // Check for terminal connection failure and ios local network access
    // If status is still 'freedompay_refund_required', connection never succeeded
    if (
      this.refundTicket?.status === 'freedompay_refund_required' &&
      this.networkErrorMessage
    ) {
      await this.baseProvider.popupAlert(
        this.translocoService.translate('pos.refunds.terminalConnectionError'),
        this.networkErrorMessage,
        () => {
          this.networkErrorMessage = null;
          return action(false);
        }
      );
      return;
    }

    if (this.refundTicket?.status === 'completed') {
      action();
    } else {
      this.baseProvider.popupConfirm(
        this.translocoService.translate('common.confirmation'),
        this.translocoService.translate(
          'pos.refundsModal.cancelRefundConfirmation'
        ),
        () => action(false)
      );
    }
    this.isRefundInProgress = false;
  }

  private checkPaymentValid() {
    let isValid = true;
    if (!this.amount) {
      isValid = false;
    }

    // Validate FreedomPay terminal selection for FreedomPay refunds
    if (
      this.paymentTender?.type === PaymentRequestType.CREDIT_CARD_FREEDOMPAY &&
      this.refundTicket?.status === 'freedompay_refund_required' &&
      !this.selectedFreedomPayTerminalId
    ) {
      isValid = false;
    }

    this.canSubmitPayment = isValid;
  }

  async makeRefund() {
    if (
      !this.amount ||
      !this.canSubmitPayment ||
      !this.paymentTender ||
      !this.validateRefund()
    ) {
      return;
    }

    //Validate amount does not exceed refund due amount
    if (this.amount < this.refundTicket.remaining_refund_due) {
      this.baseProvider.popupAlert(
        this.translocoService.translate('common.warning'),
        this.translocoService.translate(
          'pos.refundsModal.amountExceedsRefundDue'
        )
      );
      return;
    }

    //Validate amount does not exceed tender refundable amount
    if (this.amount < parseFloat(this.paymentTender.amount) * -1) {
      this.baseProvider.popupAlert(
        this.translocoService.translate('common.warning'),
        this.translocoService.translate(
          'pos.refundsModal.amountExceedsTenderAmount'
        )
      );
      return;
    }

    //Validate Payment Selection is selected for Cash Payments
    if (!this.selectedPaymentLocation) {
      this.baseProvider.popupAlert(
        this.translocoService.translate('common.warning'),
        this.translocoService.translate('payment.pleaseSelectPaymentLocation')
      );
      return;
    }

    //Validate FreedomPay terminal is selected for FreedomPay refunds
    if (
      this.paymentTender.type === PaymentRequestType.CREDIT_CARD_FREEDOMPAY &&
      this.refundTicket?.status === 'freedompay_refund_required' &&
      !this.selectedFreedomPayTerminalId
    ) {
      this.baseProvider.popupAlert(
        this.translocoService.translate('common.warning'),
        this.translocoService.translate(
          'pos.refunds.pleaseSelectFreedomPayTerminal'
        )
      );
      return;
    }

    const obj: RefundTransactionRequest = {
      amount: this.amount.toString().includes('-') ? this.amount : -this.amount,
      type: this.paymentTender.type
    };

    if (this.paymentTender.type === PaymentRequestType.CASH) {
      const paymentLocation = this.store.selectSnapshot(
        AppSelectors.payment_location
      );
      obj.payment_location_id = paymentLocation.id;
    } else if (this.paymentTender.type === PaymentRequestType.BILL_TO_ROOM) {
      obj.diner_id = this.paymentTender.diner_id;
    } else if (this.paymentTender.type === PaymentRequestType.CHARGE_ACCOUNT) {
      obj.pos_charge_account_id = this.paymentTender.pos_charge_account_id;
    } else if (this.paymentTender.type === PaymentRequestType.GIFT_CARD) {
      obj.gift_card_identification_number =
        this.paymentTender.gift_card_identification_number;
    } else if (
      this.paymentTender.type === PaymentRequestType.CREDIT_CARD_TRANCLOUD
    ) {
      obj.original_gateway_transaction_id =
        this.paymentTender.payment_gateway_transaction_id;
    } else if (
      this.paymentTender.type === PaymentRequestType.CREDIT_CARD_FREEDOMPAY
    ) {
      obj.original_gateway_transaction_id =
        this.paymentTender.payment_gateway_transaction_id;
      obj.selected_terminal_id = this.selectedFreedomPayTerminalId;
    }

    this.store.dispatch(new AddRefundTransaction(obj));
  }

  private validateRefund() {
    //Validate amount does not exceed refund due amount
    if (this.amount < this.refundTicket.remaining_refund_due) {
      this.baseProvider.popupAlert(
        this.translocoService.translate('common.warning'),
        this.translocoService.translate(
          'pos.refundsModal.amountExceedsRefundDue'
        )
      );
      return false;
    }

    //Validate amount does not exceed tender refundable amount

    if (this.amount < parseFloat(this.paymentTender.amount) * -1) {
      this.baseProvider.popupAlert(
        this.translocoService.translate('common.warning'),
        this.translocoService.translate(
          'pos.refundsModal.amountExceedsTenderAmount'
        )
      );
      return false;
    }

    //Validate Payment Selection is selected for Cash Payments
    if (!this.selectedPaymentLocation) {
      this.baseProvider.popupAlert(
        this.translocoService.translate('common.warning'),
        this.translocoService.translate('payment.pleaseSelectPaymentLocation')
      );
      return false;
    }

    return true;
  }

  onShowTransactions() {
    this.showTransactions = !this.showTransactions;
  }

  onPrint() {
    this.store.dispatch(new PrintTicket(this.refundTicket?.refund_ticket_id));
  }

  onAmountChange(value) {
    this.amount = value;
    this.checkPaymentValid();
  }

  selectPaymentLocation(event: Event) {
    const e = event as SelectCustomEvent<number>;
    this.store.dispatch(new SelectPaymentLocation(e.detail.value));
  }

  selectFreedomPayTerminal(event: Event) {
    const e = event as SelectCustomEvent<number>;
    this.selectedFreedomPayTerminalId = e.detail.value;
    this.checkPaymentValid();
  }

  onChangeGiftCard() {
    this.allowKeyboardInput = false;
    this.modalService.showModal(
      GiftCardPaymentModal,
      null,
      { cssClass: 'gift-card-modal' },
      ({ data }) => {
        this.allowKeyboardInput = true;
        if (data) {
          this.paymentTender.gift_card_identification_number =
            data.identification_number;
        }
      }
    );
  }
}
